import { useEffect } from 'react';
import { client } from '@/graphql/client';
import {
  readAllNotificationsMutation,
  removeAllNotificationsMutation,
  removeNotificationMutation
} from '@/graphql/mutation';
import { atom, useAtom } from 'jotai';
import { notificationsQuery } from '@/graphql/query';
import { usePossibleUser } from './useProfile';
import { useQuery } from '@apollo/client';
import { trpc } from '@/trpc/trpc';
import { Notification } from '@/trpc/types';

type NotificationsQuery = {
  getNotifications: Notification[];
};

const notificationsAtom = atom<Notification[]>([]);

export const useNotification = () => {
  const user = usePossibleUser();
  const [notifications, setNotifications] = useAtom(notificationsAtom);

  const { data } = useQuery<NotificationsQuery>(notificationsQuery, {
    variables: {
      page: 0,
      limit: 200
    },
    fetchPolicy: 'cache-first',
    skip: !user
  });

  trpc.notifications.useSubscription(undefined, {
    enabled: Boolean(user),
    onData: (notification) => {
      setNotifications((prev) => [notification, ...prev]);
    }
  });

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }
    if (data?.getNotifications) {
      setNotifications(data.getNotifications);
    }
  }, [data, user]);

  const clearAll = async () => {
    await client.mutate({
      mutation: removeAllNotificationsMutation
    });
    setNotifications([]);
  };

  const readAll = async () => {
    await client.mutate({
      mutation: readAllNotificationsMutation
    });
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const clearX = async (id: string) => {
    await client.mutate({
      mutation: removeNotificationMutation,
      variables: {
        id
      }
    });
    setNotifications((prev) => prev.filter((n) => n._id !== id));
  };

  const readX = async ({ id, onAction }: { id: string; onAction?: () => void }) => {
    await client.mutate({
      mutation: readAllNotificationsMutation,
      variables: {
        id
      }
    });
    setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
    onAction && onAction();
  };

  return {
    notifications,
    clearAll,
    readAll,
    clearX,
    readX
  };
};
